/**
 * Live Fleet Timeseries page.
 *
 * Behaves like the day Fleet Timeseries when it's split-view-with-map: shows only the active time window and
 * animates with the live clock — but sourced from the LIVE feed with a true live tail (new telemetry appears at
 * the leading edge in real time). It loads the SAME saved `fleet_timeseries` chart objects and renders them via
 * the shared `TimeSeries` renderer in its opt-in uPlot mode.
 *
 * DATA PATH — two tiers, meeting in one append-only buffer:
 *
 *  - **Tail.** The columnar Arrow stream (`arrowStreamClient`, shared with the live map) decodes straight into
 *    `liveColumnarStore`. There is no frame callback to hook, so `liveColumnarTailReader` drains that ring
 *    incrementally — cursor per source, only what is new since the last tick. Subscribing also triggers a
 *    server snapshot of its recent ring, which the first (cold) drain picks up whole.
 *  - **Backfill.** The stream ring only holds a couple of minutes, so any wider window is filled once over HTTP
 *    from `/api/stream/sources/:name/data/merged`, which stitches durable Parquet to the server's in-memory ring
 *    (`server_stream/controllers/tieredRead.js`).
 *
 * Both feed `liveTimeseriesBuffer`, which keeps one ascending array per (chart, series, boat) and only accepts
 * strictly-newer points — so the two tiers can overlap freely and a re-sent snapshot after a reconnect is a
 * no-op. That is also why the tick costs O(new points) instead of O(window): the previous implementation
 * re-materialized and re-sorted every series on every animation frame.
 *
 * The redraw tick is throttled to LIVE_REDRAW_MS (leading + trailing) and must stay SLOWER than the 100 ms
 * debounce inside `TimeSeries.processDataForCharts`, which DROPS rather than defers — pushing per frame would
 * lose most updates non-deterministically.
 */

import { createSignal, createMemo, createEffect, onMount, onCleanup, untrack, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";

import TimeSeries from "../../../../components/charts/TimeSeries";
import TimeSeriesSettings from "../../../../components/menus/TimeSeriesSettings";
import LoadingOverlay from "../../../../components/utilities/Loading";

import { persistantStore } from "../../../../store/persistantStore";
import { user } from "../../../../store/userStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { streamingStatusStore } from "../../../../store/streamingStatusStore";
import { liveConfigStore } from "../../../../store/liveConfigStore";
import { liveColumnarStore } from "../../../../store/liveColumnarStore";
import {
  setSelectedTime,
  isPlaying,
  setIsPlaying,
  timeWindow,
  liveFollowActive,
  getLiveDisplayTargetMs,
  isLiveReplayPlayheadReady,
  snapLiveSmoothPlaybackToMs,
} from "../../../../store/playbackStore";
import { arrowStreamClient } from "../../../../services/arrowStreamClient";
import { streamingDataService } from "../../../../services/streamingDataService";
import { ensureLiveDatasetsForProject } from "../../../../utils/liveDatasets";
import { loadSharedSourceIds, saveSharedSourceIds } from "../../../../utils/liveSourcePersistence";
import { setupMediaContainerScaling } from "../../../../utils/global";
import { logPageLoad } from "../../../../utils/logging";
import { debug, warn } from "../../../../utils/console";
import { resolveTrackColor } from "../../../../components/charts/map/renderers/boatMarkerStyle";
import {
  createLiveTimeseriesBuffer,
  liveSeriesKey,
  type LiveSeriesPointSpec,
} from "../../../../utils/liveTimeseriesBuffer";
import { createColumnarTailReader } from "../../../../utils/liveColumnarTailReader";
import { createStallBackoff, LIVE_POLL_BASE_MS, LIVE_POLL_MAX_MS } from "../../../../utils/liveStallBackoff";
import {
  liveBackfillMinutesForWindow,
  liveEffectiveWindowMinutes,
  liveRetentionMsForWindow,
} from "../../../../constants/liveRetention";
import {
  fetchFleetTimeseriesChartObjects,
  extractRequiredChannels,
  type FleetSource,
} from "../../../../utils/fleetTimeseriesConfig";

const { selectedClassName, selectedProjectId, selectedPage, setSelectedPage } = persistantStore;

/** Default active window (min) when the store timeWindow is 0/Full. */
const LIVE_TS_DEFAULT_WINDOW_MIN = 2;
/** Extra span below the window start we prime/keep, so playhead preroll never shows an empty left edge. */
const WINDOW_GUARD_MS = 60_000;
/** Paint the standalone playhead this far behind the live edge (no map to anchor the clock). */
const LIVE_PLAYHEAD_PREROLL_MS = 15_000;
const CONSUMER_ID = "live-timeseries";
/** Uniform plot height (px) for every chart, so fleet ALL-source charts don't scale to wildly different sizes. */
const LIVE_TS_CHART_HEIGHT_PX = 380;
/**
 * Redraw cadence. Must stay slower than the 100 ms debounce in TimeSeries.processDataForCharts, which
 * DROPS a too-soon call rather than deferring it.
 */
const LIVE_REDRAW_MS = 250;
/**
 * Window-slide cadence, ms. The DATA tick stays at LIVE_REDRAW_MS; this only moves the x scale, which
 * is a `setScale` per plot and no re-materialization. ~30 fps reads as smooth without paying 60.
 */
const LIVE_SLIDE_MS = 33;
/**
 * Assumed gap between data waves until we have measured one, ms.
 *
 * The server delivers deltas on a poll cycle, so samples land in batches covering the last several
 * seconds — not one at a time. Drawing each batch the instant it arrives is what makes the trace
 * grow in blocks: the window glides over empty space, then a whole wave appears at once.
 */
const LIVE_WAVE_MS_INITIAL = 5_000;
/** Bounds on the measured wave interval, so one stalled or bunched delivery can't distort the pacing. */
const LIVE_WAVE_MS_MIN = 500;
const LIVE_WAVE_MS_MAX = 10_000;
/** Smoothing on the wave-interval estimate (0..1; higher = more responsive, noisier). */
const LIVE_WAVE_EMA_ALPHA = 0.3;

/** One drawn line: one channel of one chart for one boat. */
interface LivePlanEntry {
  chartIndex: number;
  seriesIndex: number;
  /** APP source id — what the buffer and the backfill Map are keyed on. */
  sourceId: number;
  /** STREAM/frame source id — what the columnar ring is keyed on. NOT interchangeable with sourceId. */
  frameSourceId: number | undefined;
  sourceName: string;
  key: string;
  spec: LiveSeriesPointSpec;
  /** The saved series with its per-source colour/label applied; `data` is filled per tick. */
  baseSeries: any;
  lineType: unknown;
}

/**
 * Expand saved chart objects into one plan entry per drawn line.
 *
 * Mirrors `expandChartObjectsToConfig`'s expansion rules exactly — including the `"<channel> - <BOAT>"`
 * label for ALL-source series, which is load-bearing: the renderer's `channelNameFromLabel` splits on
 * that exact `" - "` to recover the channel for the in-plot caption.
 */
function buildLivePlan(
  chartObjects: any[],
  sources: FleetSource[],
  sourceColorMap: Map<string, string>
): LivePlanEntry[] {
  const plan: LivePlanEntry[] = [];

  (chartObjects || []).forEach((chart: any, chartIndex: number) => {
    (chart.series || []).forEach((series: any, seriesIndex: number) => {
      const xName = series.xaxis?.name;
      const yName = series.yaxis?.name;
      if (!xName || !yName) return;

      const colorBySource = series.colorBySource;
      let seriesSources: FleetSource[];
      let renameBySource = false;

      if (colorBySource === "ALL" || colorBySource === "all") {
        seriesSources = sources;
        renameBySource = true;
      } else if (colorBySource) {
        const specific = sources.find(
          (s) => s.source_name.toLowerCase() === String(colorBySource).toLowerCase()
        );
        seriesSources = specific ? [specific] : [];
      } else {
        seriesSources = sources.length > 0 ? [sources[0]] : [];
      }

      for (const source of seriesSources) {
        const color =
          (renameBySource ? sourceColorMap.get(source.source_name.toLowerCase()) : null) ||
          series.color ||
          "#1f77b4";
        plan.push({
          chartIndex,
          seriesIndex,
          sourceId: source.source_id,
          frameSourceId: liveColumnarStore.sourceId(source.source_name),
          sourceName: source.source_name,
          key: liveSeriesKey(chartIndex, seriesIndex, source.source_id),
          // The point is keyed under the ORIGINAL channel name (what the row carries), matching
          // expandChartObjectsToConfig; only the series LABEL gets the boat suffix.
          spec: { xName, yName, yChannel: yName },
          baseSeries: {
            ...series,
            yaxis: renameBySource
              ? { ...series.yaxis, name: `${yName} - ${source.source_name}` }
              : series.yaxis,
            color,
          },
          lineType: series.lineType,
        });
      }
    });
  });

  return plan;
}

export default function LiveFleetTimeseriesPage(props: any) {
  let navigate: ((path: string) => void) | null;
  try {
    navigate = useNavigate();
  } catch {
    navigate = null;
  }

  let mediaContainerRef: HTMLDivElement | undefined;
  let disposed = false;
  let cleanupScaling: (() => void) | null = null;

  const buffer = createLiveTimeseriesBuffer();
  const tail = createColumnarTailReader();
  const backfillBackoff = createStallBackoff({ baseMs: LIVE_POLL_BASE_MS, maxMs: LIVE_POLL_MAX_MS });

  const [chartObjects, setChartObjects] = createSignal<any[]>([]);
  const [ready, setReady] = createSignal(false);
  const [selectedSourceIds, setSelectedSourceIds] = createSignal<Set<number>>(new Set());
  /** Latched once any data has landed, so the chart mounts with points rather than an empty set. */
  const [dataReady, setDataReady] = createSignal(false);
  /** Built chart configs handed to TimeSeries. */
  const [liveCharts, setLiveCharts] = createSignal<any[] | null>(null);
  /** Right edge of the visible window, published on the redraw tick (NOT per sample). */
  const [liveAnchorMs, setLiveAnchorMs] = createSignal<number | null>(null);
  const [streamConnected, setStreamConnected] = createSignal(true);

  const objectName = (): string => {
    const page = (selectedPage() || "").trim();
    if (page && page !== "default") return page;
    const prop = (props?.objectName || "").trim();
    if (prop && prop !== "default") return prop;
    return "";
  };

  // ---- Source selection (the SHARED fleetPerformanceSources selection, same as the report pages) ----
  // Deliberately not the live map's selection: the map owns its own `liveMapSources` key so that
  // choosing boats to watch on the map does not re-select them here and on every report page.
  const dataSourcesOptions = createMemo(() => {
    if (!sourcesStore.isReady()) return [];
    const projectSources = sourcesStore.sources();
    if (!projectSources || projectSources.length === 0) return [];
    return untrack(() =>
      projectSources
        .map((s) => {
          const id = Number(s.source_id);
          if (!Number.isFinite(id)) return null;
          const getter = () => selectedSourceIds().has(id);
          const setter = (value: any) => {
            if (value instanceof Set) {
              setSelectedSourceIds(new Set(Array.from(value).map(Number)));
              return;
            }
            const next = new Set(selectedSourceIds());
            const on = typeof value === "function" ? value(next.has(id)) : value;
            if (on) next.add(id);
            else next.delete(id);
            setSelectedSourceIds(next);
          };
          return {
            key: `source-${id}`,
            label: s.source_name || `Source ${id}`,
            type: "toggle",
            signal: [getter, setter] as [() => boolean, (v: boolean) => void],
          };
        })
        .filter((opt): opt is NonNullable<typeof opt> => opt !== null)
    );
  });

  // Restore the shared persisted selection so the page works standalone.
  let hasRestoredSources = false;
  let restoreComplete = false;
  createEffect(() => {
    const options = dataSourcesOptions();
    if (options.length === 0 || hasRestoredSources) return;
    const validIds = new Set(
      options
        .map((opt) => {
          const m = opt!.key.match(/source-(\d+)/);
          return m ? Number(m[1]) : null;
        })
        .filter((id): id is number => id !== null)
    );
    if (validIds.size === 0) return;
    hasRestoredSources = true;
    void (async () => {
      try {
        const restored = await loadSharedSourceIds();
        const filtered = new Set<number>(Array.from(restored).filter((id) => validIds.has(id)));
        setSelectedSourceIds(filtered.size > 0 ? filtered : new Set<number>());
      } finally {
        restoreComplete = true;
      }
    })();
  });

  // Persist + share selection.
  createEffect(() => {
    if (!restoreComplete) return;
    const ids = selectedSourceIds();
    if (ids.size === 0) return;
    if (!(selectedClassName?.() || "")) return;
    void saveSharedSourceIds(ids);
  });

  /** Selected sources as FleetSource[] (dataset_id unused by the live feed — keyed by name). */
  const sourcesToUse = createMemo<FleetSource[]>(() => {
    const ids = selectedSourceIds();
    if (!sourcesStore.isReady() || ids.size === 0) return [];
    const out: FleetSource[] = [];
    for (const id of ids) {
      const name = sourcesStore.getSourceName(id);
      if (name) out.push({ source_id: Number(id), source_name: name, dataset_id: 0 });
    }
    return out;
  });

  const requiredChannels = createMemo(() => extractRequiredChannels(chartObjects()));
  /** Channels to subscribe on the Arrow stream (drop Datetime — rows carry their own timestamp). */
  const streamChannels = createMemo(() => requiredChannels().filter((c) => c !== "Datetime"));

  const effectiveTimeWindow = createMemo(() => {
    const w = Number(timeWindow());
    if (!Number.isFinite(w) || w <= 0) return LIVE_TS_DEFAULT_WINDOW_MIN;
    return liveEffectiveWindowMinutes(w);
  });

  /**
   * Per-boat line colours, taken through the same black->white override the maps use so a black team
   * colour (NZL) draws white on the chart and in its legend swatch.
   */
  const sourceColorMap = createMemo(() => {
    const map = new Map<string, string>();
    for (const s of sourcesToUse()) {
      const color = sourcesStore.getSourceColor(s.source_name) || "#1f77b4";
      map.set(s.source_name.toLowerCase(), resolveTrackColor(s.source_name, color));
    }
    return map;
  });

  /**
   * The drawn lines. Recomputed only when the chart objects or the boat selection change — NOT per tick.
   * Tracks the columnar epoch too, because `frameSourceId` is unresolvable until the source registry
   * arrives on the socket.
   */
  const plan = createMemo<LivePlanEntry[]>(() => {
    const objs = chartObjects();
    const sources = sourcesToUse();
    if (!objs.length || sources.length === 0) return [];
    void liveColumnarStore.epoch();
    return buildLivePlan(objs, sources, sourceColorMap());
  });

  // Forget buffered lines that are no longer drawn (chart switched, boat deselected).
  createEffect(() => {
    const entries = plan();
    buffer.retainOnly(entries.map((e) => e.key));
  });

  // ---- Live feed: subscribe (server replies with a snapshot, then deltas) -------------------------------
  createEffect(() => {
    const sources = sourcesToUse();
    const chans = streamChannels();
    if (sources.length === 0 || chans.length === 0) return;
    arrowStreamClient.subscribe(
      CONSUMER_ID,
      sources.map((s) => s.source_name),
      chans
    );
  });

  // ---- Backfill: one HTTP read per boat for anything deeper than the stream ring holds ------------------
  let backfillKey = "";
  let backfillInFlight = false;
  let lastBackfillAttemptAt = 0;

  const runBackfill = async () => {
    const sources = sourcesToUse();
    const entries = plan();
    const chans = requiredChannels();
    if (sources.length === 0 || entries.length === 0 || chans.length === 0) return;

    const minutes = liveBackfillMinutesForWindow(timeWindow());
    const key = `${sources.map((s) => s.source_id).sort().join(",")}|${chans.join(",")}|${minutes}`;
    if (key === backfillKey || backfillInFlight) return;

    // Back off while the feed is stalled rather than re-reading Parquet at full rate.
    const now = Date.now();
    if (now - lastBackfillAttemptAt < backfillBackoff.delayMs()) return;
    lastBackfillAttemptAt = now;

    backfillInFlight = true;
    try {
      const endMs = liveColumnarStore.latestTs(frameIds()) ?? Date.now();
      const startMs = endMs - minutes * 60_000 - WINDOW_GUARD_MS;
      const rowsBySource = await streamingDataService.fetchMultipleSources(
        sources.map((s) => s.source_name),
        chans,
        startMs,
        endMs
      );
      if (disposed) return;

      let seeded = 0;
      for (const entry of entries) {
        const rows = rowsBySource.get(entry.sourceId);
        if (!rows || rows.length === 0) continue;
        // seedRows (not appendRows) so a wider window can re-seed BELOW what the tail already holds;
        // the tail's next drain re-appends the newer points on top.
        seeded += buffer.seedRows(entry.key, rows as any[], entry.spec);
      }
      backfillBackoff.observeEdge(buffer.latestTs());
      if (seeded > 0) {
        backfillKey = key;
        setDataReady(true);
        scheduleFlush();
      }
      debug("[LiveTimeseries] backfill", { minutes, sources: sources.length, seeded });
    } catch (err) {
      // The live tail still fills the chart, so a failed backfill is not an error state.
      debug("[LiveTimeseries] backfill failed (tail continues)", err);
      backfillBackoff.observeEdge(null);
    } finally {
      backfillInFlight = false;
    }
  };

  /** Frame (stream) ids for the selected sources, resolved by name from the columnar source registry. */
  const frameIds = (): Set<number> => {
    const set = new Set<number>();
    for (const s of sourcesToUse()) {
      const fid = liveColumnarStore.sourceId(s.source_name);
      if (fid !== undefined) set.add(fid);
    }
    return set;
  };

  // ---- The redraw tick ----------------------------------------------------------------------------------
  let lastTickAt = 0;
  let tickTimer: number | null = null;
  /** Newest sample timestamp seen, and the wall time it landed — the basis for paced reveal. */
  let lastDataTs = 0;
  let lastDataArrivedAtMs = 0;
  /** Rolling estimate of the gap between data waves, measured from actual arrivals. */
  let waveMs = LIVE_WAVE_MS_INITIAL;
  /** Anchor is monotonic — a late wave must stall it, never rewind the window. */
  let lastAnchorMs = 0;
  let slideRafId: number | null = null;
  let lastSlideAt = 0;

  const flushCharts = async () => {
    const objs = chartObjects();
    const entries = plan();
    if (!objs.length || entries.length === 0) return;

    // Drain the columnar tail ONCE PER SOURCE, then fan the same rows out to every line on that boat.
    // Draining per entry would hand every line after the first an empty array (the cursor has moved).
    const coldStartMs =
      (liveColumnarStore.latestTs(frameIds()) ?? Date.now()) -
      effectiveTimeWindow() * 60_000 -
      WINDOW_GUARD_MS;
    const rowsByFrameId = new Map<number, Record<string, unknown>[]>();
    for (const fid of frameIds()) {
      rowsByFrameId.set(fid, tail.drain(fid, coldStartMs));
    }
    for (const entry of entries) {
      if (entry.frameSourceId === undefined) continue;
      const rows = rowsByFrameId.get(entry.frameSourceId);
      if (rows && rows.length > 0) buffer.appendRows(entry.key, rows, entry.spec);
    }

    const latest = buffer.latestTs();
    if (latest != null) {
      buffer.prune(latest - liveRetentionMsForWindow(timeWindow()));
      if (!dataReady()) setDataReady(true);
    }

    // Paused: freeze. Freezing the x window alone is not enough — uPlot ranges y over ALL the data it
    // holds, so a "paused" chart still visibly breathes as samples land. Appends keep filling the
    // buffer, so resuming is seamless.
    if (!liveFollowActive() && liveCharts() != null) return;

    if (latest != null) {
      // Note WHEN this wave landed in wall time, and how long since the previous one — the slide
      // loop paces the reveal to that interval.
      if (latest !== lastDataTs) {
        const now = Date.now();
        if (lastDataArrivedAtMs > 0) {
          const observed = now - lastDataArrivedAtMs;
          if (observed >= LIVE_WAVE_MS_MIN && observed <= LIVE_WAVE_MS_MAX) {
            waveMs = waveMs * (1 - LIVE_WAVE_EMA_ALPHA) + observed * LIVE_WAVE_EMA_ALPHA;
          }
        }
        lastDataTs = latest;
        lastDataArrivedAtMs = now;
      }
      setLiveAnchorMs(currentAnchorMs());
    }

    const dataByKey = new Map<string, unknown[]>();
    await Promise.all(
      entries.map(async (entry) => {
        dataByKey.set(entry.key, await buffer.materialize(entry.key, entry.lineType, Number.POSITIVE_INFINITY));
      })
    );
    if (disposed) return;

    setLiveCharts(
      objs.map((chart: any, chartIndex: number) => ({
        ...chart,
        chart: `chart_${chartIndex + 1}`,
        chartHeight: LIVE_TS_CHART_HEIGHT_PX,
        series: entries
          .filter((e) => e.chartIndex === chartIndex)
          .map((e) => ({ ...e.baseSeries, data: dataByKey.get(e.key) ?? [] })),
      }))
    );
  };

  const fireTick = () => {
    tickTimer = null;
    lastTickAt = Date.now();
    void flushCharts();
  };

  /** Leading + trailing throttle: redraw at most once per LIVE_REDRAW_MS, never dropping the last update. */
  const scheduleFlush = () => {
    const waitMs = LIVE_REDRAW_MS - (Date.now() - lastTickAt);
    if (waitMs <= 0) {
      if (tickTimer != null) clearTimeout(tickTimer);
      fireTick();
      return;
    }
    if (tickTimer == null) tickTimer = window.setTimeout(fireTick, waitMs);
  };

  /**
   * Smooth window slide.
   *
   * The feed lands in waves (the server flushes on a poll cycle, ~5 s), so an anchor pinned to the
   * newest sample makes the whole trace jump one wave-width at a time. Between waves the anchor is
   * extrapolated with wall-clock, which turns those jumps into an even glide — the same trick the
   * live map uses for boat positions.
   *
   * This is cheap on purpose: it moves ONLY the x scale (a `setScale` per plot, handled by
   * `applyStreamingAnchor` in the renderer). No row materialization, no series rebuild, no legend
   * recompute — those stay on the LIVE_REDRAW_MS data tick. It also stops the moment follow is off,
   * so a paused chart costs nothing.
   */
  /**
   * Where the window's right edge should be right now.
   *
   * The window deliberately TRAILS the newest sample by one wave, and glides forward across that
   * wave over the time the next one takes to arrive. So there is always ~a wave of already-received
   * data sitting just off-screen to the right, and the trace grows continuously as the edge sweeps
   * over it — instead of the window running ahead into empty space and a whole block appearing at
   * once when the next delivery lands.
   *
   * Clamped to the real data edge so the window can never show more than we actually have, and
   * monotonic so a late wave stalls it rather than rewinding the view.
   */
  const currentAnchorMs = (): number => {
    if (lastDataTs <= 0) return 0;
    const elapsed = Math.max(0, Date.now() - lastDataArrivedAtMs);
    const target = Math.min(lastDataTs, lastDataTs - waveMs + elapsed);
    const next = Math.max(lastAnchorMs, target);
    lastAnchorMs = next;
    return next;
  };

  const slideTick = () => {
    slideRafId = null;
    if (disposed) return;

    if (!liveFollowActive() || lastDataTs <= 0) {
      // Not following (or nothing yet): idle without rescheduling; the data tick restarts us.
      return;
    }

    const now = Date.now();
    if (now - lastSlideAt >= LIVE_SLIDE_MS) {
      lastSlideAt = now;
      setLiveAnchorMs(currentAnchorMs());
    }
    slideRafId = requestAnimationFrame(slideTick);
  };

  const ensureSliding = () => {
    if (slideRafId == null && !disposed) slideRafId = requestAnimationFrame(slideTick);
  };

  // New columnar data (or a plan change) schedules a redraw. Tracks the epoch only — never the smooth clock.
  createEffect(() => {
    void liveColumnarStore.epoch();
    void plan();
    scheduleFlush();
    ensureSliding();
    void runBackfill();
  });

  // Re-arm the slide loop when follow is re-engaged (it self-stops while paused).
  createEffect(() => {
    if (liveFollowActive()) ensureSliding();
  });

  // Re-backfill when the requested depth changes.
  createEffect(() => {
    void timeWindow();
    void runBackfill();
  });

  // ---- Connection state --------------------------------------------------------------------------------
  // Keep the chart MOUNTED on a drop: resetting dataReady would unmount it and discard the buffer, which
  // is the opposite of what this page is for. Just show a badge, and re-drain the fresh snapshot on return.
  onMount(() => {
    const off = arrowStreamClient.onConnection((connected) => {
      setStreamConnected(connected);
      if (connected) {
        tail.reset();
        backfillKey = "";
        scheduleFlush();
      }
    });
    onCleanup(off);
  });

  // ---- Standalone playhead anchor: only when the live map isn't already driving the clock ---------------
  let anchored = false;
  createEffect(() => {
    if (anchored) return;
    if (sourcesToUse().length === 0) return;
    void liveColumnarStore.epoch();
    const frameIdSet = frameIds();
    const latest = liveColumnarStore.latestTs(frameIdSet) ?? 0;
    if (!latest) return;
    // The live map (if co-mounted) anchors the clock and starts playback; don't fight it.
    if (isPlaying()) {
      anchored = true;
      return;
    }
    const targetMs = getLiveDisplayTargetMs(frameIdSet);
    if (!isLiveReplayPlayheadReady(targetMs)) return;
    const startMs = Math.max(0, targetMs - LIVE_PLAYHEAD_PREROLL_MS);
    setSelectedTime(new Date(startMs), "playback", true);
    snapLiveSmoothPlaybackToMs(startMs);
    setIsPlaying(true);
    anchored = true;
    debug("[LiveTimeseries] anchored standalone playhead", { startMs, targetMs, latest });
  });

  // ---- Lifecycle ---------------------------------------------------------------------------------------
  let chartChannelSig = "";
  let chartReloadTimer: number | null = null;
  let chartLoadInFlight = false;

  const loadChartObjects = async (initial: boolean) => {
    if (!initial && chartLoadInFlight) return;
    chartLoadInFlight = true;
    const currentUser = user();
    if (!currentUser?.user_id) {
      chartLoadInFlight = false;
      if (initial && !disposed) setReady(true);
      return;
    }
    try {
      const { chartObjects: objs, resolvedName } = await fetchFleetTimeseriesChartObjects({
        className: selectedClassName(),
        projectId: selectedProjectId(),
        datasetId: 0,
        userId: currentUser.user_id,
        preferredName: objectName() || null,
      });
      if (disposed) return;
      if (initial && resolvedName) setSelectedPage(resolvedName);
      const sig = extractRequiredChannels(objs).join("\n");
      if (!initial && sig === chartChannelSig) return;
      chartChannelSig = sig;
      if (!initial) {
        backfillKey = "";
        tail.reset();
        debug("[LiveTimeseries] chart channels changed", {
          channels: extractRequiredChannels(objs).filter((c) => c !== "Datetime"),
        });
      } else {
        debug("[LiveTimeseries] loaded chart objects", {
          count: objs.length,
          channels: extractRequiredChannels(objs).filter((c) => c !== "Datetime"),
        });
      }
      setChartObjects(objs);
    } catch (err) {
      if (initial) warn("[LiveTimeseries] failed to load chart objects", err);
      else debug("[LiveTimeseries] chart refresh failed", err);
    } finally {
      chartLoadInFlight = false;
      if (initial && !disposed) setReady(true);
    }
  };

  onMount(() => {
    void logPageLoad("FleetTimeseries.tsx", "Live Time Series", "Loaded");
    streamingStatusStore.initialize();
    liveConfigStore.fetchLiveConfig().catch(() => {});
    ensureLiveDatasetsForProject(Number(selectedProjectId()), selectedClassName()).catch(() => {});
    void loadChartObjects(true);
    // The builder saves into the same chart object. Pick up a newly added channel without a
    // full reload so the stream subscribe (and the supplemental poll) widens while this page is open.
    chartReloadTimer = window.setInterval(() => {
      void loadChartObjects(false);
    }, 5000);

    cleanupScaling = setupMediaContainerScaling({
      scaleToWidth: true,
      logPrefix: "LiveTimeseries",
      getMediaContainer: () => mediaContainerRef ?? null,
    });
  });

  // Keep live config synced to streaming status (same as LiveMap). `status()` returns the accessor, so call
  // it again to read (and reactively track) the snapshot.
  createEffect(() => {
    const snap = streamingStatusStore.status()();
    liveConfigStore.syncLiveConfigFromStatus({
      streamingStarted: snap.streamingStarted,
      simulationActive: snap.simulationActive,
      isLiveProcessingActive: snap.isLiveProcessingActive,
    });
  });

  onCleanup(() => {
    disposed = true;
    if (chartReloadTimer != null) {
      clearInterval(chartReloadTimer);
      chartReloadTimer = null;
    }
    if (tickTimer != null) {
      clearTimeout(tickTimer);
      tickTimer = null;
    }
    if (slideRafId != null) {
      cancelAnimationFrame(slideRafId);
      slideRafId = null;
    }
    buffer.clear();
    // Release (NOT stop) so a co-mounted live map keeps its stream.
    try {
      arrowStreamClient.release(CONSUMER_ID);
    } catch {
      /* noop */
    }
    if (cleanupScaling) {
      try {
        cleanupScaling();
      } catch {
        /* noop */
      }
      cleanupScaling = null;
    }
    // NOTE: do not toggle liveMode here — Window.tsx owns it for /live/ pages and the map may still be mounted.
  });

  return (
    <div
      id="media-container"
      class="timeseries-page"
      ref={mediaContainerRef}
      style={{ "overflow-x": "hidden", display: "flex", "flex-direction": "column", "min-height": "0" }}
    >
      <div class="container relative flex-1 min-h-0" style={{ "overflow-x": "hidden", "max-width": "100%" }}>
        <div style="position: absolute; top: -5px; left: 0; z-index: 10;">
          <TimeSeriesSettings
            isFleet={true}
            objectName={objectName()}
            useIconTrigger={true}
            dataSourcesOptions={dataSourcesOptions()}
            filterConfig={{ showRaces: false, showLegs: false, showGrades: false }}
          />
        </div>
        <Show when={!streamConnected()}>
          <div
            class="absolute top-0 right-2 z-20 text-xs px-2 py-1 rounded bg-amber-600/80 text-white"
            title="The live stream dropped; the chart is frozen at the last data received."
          >
            Reconnecting…
          </div>
        </Show>
        <div class="timeseries-hover-zone">
          <div class="performance-charts-scroll-container">
            <div
              id="timeseries-area"
              style={{ width: "100%", height: "100%", "min-height": "100%", "padding-top": "20px", "overflow-x": "hidden" }}
            >
              <Show
                when={ready()}
                fallback={<LoadingOverlay message="Loading live time series…" showProgress={false} />}
              >
                <Show
                  when={chartObjects().length > 0}
                  fallback={
                    <div class="flex flex-col items-center justify-center h-screen min-h-[500px] text-center p-8">
                      <h3 class="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        No Time Series Chart Configured
                      </h3>
                      <p class="text-gray-500 dark:text-gray-400 mb-6 max-w-md">
                        Create a fleet time series chart to view it live.
                      </p>
                      <button
                        onClick={() => navigate && navigate(`/timeseries-builder?object_name=${objectName()}&fleet=true`)}
                        class="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                      >
                        Create Time Series Chart
                      </button>
                    </div>
                  }
                >
                  {/* Wait for the first data (snapshot or backfill) before mounting, so the renderer wires
                      its uPlot instances against real data rather than an empty set. */}
                  <Show
                    when={dataReady() && liveCharts()}
                    fallback={
                      <Show
                        when={sourcesToUse().length > 0}
                        fallback={
                          <div class="flex items-center justify-center h-full min-h-[300px] text-gray-500 dark:text-gray-400 text-center p-8">
                            Select one or more sources in settings to view live time series.
                          </div>
                        }
                      >
                        <LoadingOverlay message="Loading live window…" showProgress={false} />
                      </Show>
                    }
                  >
                    <TimeSeries
                      chart={liveCharts()}
                      useUPlotRenderer={true}
                      suppressInternalFetch={true}
                      showLegendTable={true}
                      suppressSelectionStore={true}
                      stickyYRange={true}
                      timeWindowMinutes={effectiveTimeWindow()}
                      stickyXWindowMs={effectiveTimeWindow() * 60_000}
                      stickyXAnchorMs={liveAnchorMs()}
                      hideSelectedTimeLine={liveFollowActive()}
                      disableTimeInteractions={liveFollowActive()}
                    />
                  </Show>
                </Show>
              </Show>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
